/**
 * Minimal, XSS-safe markdown renderer for Marlow's messages.
 *
 * Ported from Profound Thinking's (itself descended from Profound Living's
 * coaching renderer); only the inline colours differ, re-toned for this app's
 * slate-and-campfire canvas. The run-segmentation below is the fix for the
 * fleet's recurring "markdown *" defect — models routinely run an intro line
 * and its bullets together in one block, and the older every-line gates
 * rendered those as paragraphs with literal asterisks.
 *
 * Australian English throughout.
 */

import React from 'react';

// Interleave <br /> elements for newlines inside inline content, so emphasis
// can span a soft line break without losing the break itself.
function withBreaks(str, keyPrefix) {
  const parts = String(str).split('\n');
  if (parts.length === 1) return str;
  return parts.flatMap((part, idx) =>
    idx === 0 ? [part] : [<br key={`${keyPrefix}-nl-${idx}`} />, part]
  );
}

function formatInline(text, keyPrefix) {
  if (!text) return null;
  const nodes = [];
  let i = 0;
  let buf = '';
  const push = (node) => {
    if (buf) { nodes.push(withBreaks(buf, `${keyPrefix}-t-${i}`)); buf = ''; }
    nodes.push(node);
  };

  while (i < text.length) {
    const rest = text.slice(i);

    let m = rest.match(/^\*\*([^*]+)\*\*/);
    if (m) { push(<strong key={`${keyPrefix}-b-${i}`}>{withBreaks(m[1], `${keyPrefix}-b-${i}`)}</strong>); i += m[0].length; continue; }

    // Emphasis may span a soft line break (models wrap emphasised sentences).
    // (?!\s) stops a lone "* " — a stray bullet or arithmetic asterisk — from
    // pairing with a genuine opener later in the paragraph.
    m = rest.match(/^\*(?!\s)([^*]+?)\*/) || rest.match(/^_(?!\s)([^_\n]+)_/);
    if (m) { push(<em key={`${keyPrefix}-i-${i}`}>{withBreaks(m[1], `${keyPrefix}-i-${i}`)}</em>); i += m[0].length; continue; }

    m = rest.match(/^`([^`\n]+)`/);
    if (m) {
      push(<code key={`${keyPrefix}-c-${i}`} className="px-1 py-0.5 rounded bg-white/10 text-slate-100 text-[0.95em]">{m[1]}</code>);
      i += m[0].length; continue;
    }

    m = rest.match(/^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/);
    if (m) {
      push(
        <a key={`${keyPrefix}-a-${i}`} href={m[2]} target="_blank" rel="noreferrer noopener"
           className="underline text-amber-300 hover:text-white">{m[1]}</a>
      );
      i += m[0].length; continue;
    }

    buf += text[i];
    i += 1;
  }
  if (buf) nodes.push(withBreaks(buf, `${keyPrefix}-t-end`));
  return nodes;
}

export function renderMarkdown(text) {
  if (!text || typeof text !== 'string') return null;

  const blocks = text.replace(/\r\n/g, '\n').split(/\n{2,}/);
  const out = [];

  blocks.forEach((block, bIdx) => {
    const lines = block.split('\n').map(l => l.replace(/\s+$/, ''));
    if (lines.length === 0 || (lines.length === 1 && !lines[0])) return;

    // Blockquote: every line starts with ">". Marlow quotes the teller's own
    // best line back at them constantly, so this one earns its keep here.
    if (lines.every(l => /^\s*>\s?/.test(l))) {
      const inner = lines.map(l => l.replace(/^\s*>\s?/, '')).join('\n');
      out.push(
        <blockquote key={`q-${bIdx}`} className="border-l-2 border-amber-400/60 pl-4 my-2 text-slate-300 italic">
          {formatInline(inner, `q-${bIdx}`)}
        </blockquote>
      );
      return;
    }

    // Segment the block into runs of one kind — heading, bullet, numbered,
    // or plain text — so a bullet list that opens without a blank line above
    // it still renders as a list.
    const kindOf = (l) =>
      /^#{1,3}\s+\S/.test(l) ? 'h'
        : /^\s*[-*]\s+/.test(l) ? 'ul'
          : /^\s*\d+[.)]\s+/.test(l) ? 'ol'
            : 'p';

    let runStart = 0;
    for (let idx = 1; idx <= lines.length; idx++) {
      if (idx < lines.length && kindOf(lines[idx]) === kindOf(lines[runStart])) continue;
      const run = lines.slice(runStart, idx);
      const kind = kindOf(run[0]);
      const rKey = `${bIdx}-${runStart}`;

      if (kind === 'h') {
        // Headings downshift to h3/h4 so Marlow's section heads sit inside the
        // page hierarchy rather than competing with it.
        run.forEach((l, li) => {
          const h = l.match(/^(#{1,3})\s+(.+)$/);
          const level = h[1].length;
          const cls = level === 1
            ? 'text-2xl font-bold text-white mt-3 mb-1'
            : level === 2
              ? 'text-xl font-bold text-white mt-3 mb-1'
              : 'text-lg font-semibold text-slate-100 mt-2 mb-1';
          const Tag = level === 1 ? 'h3' : 'h4';
          out.push(<Tag key={`h-${rKey}-${li}`} className={cls}>{formatInline(h[2], `h-${rKey}-${li}`)}</Tag>);
        });
      } else if (kind === 'ul') {
        out.push(
          <ul key={`b-${rKey}`} className="list-disc pl-5 space-y-1 my-1">
            {run.map((l, li) => (
              <li key={li}>{formatInline(l.replace(/^\s*[-*]\s+/, ''), `b-${rKey}-${li}`)}</li>
            ))}
          </ul>
        );
      } else if (kind === 'ol') {
        out.push(
          <ol key={`b-${rKey}`} className="list-decimal pl-5 space-y-1 my-1">
            {run.map((l, li) => (
              <li key={li}>{formatInline(l.replace(/^\s*\d+[.)]\s+/, ''), `b-${rKey}-${li}`)}</li>
            ))}
          </ol>
        );
      } else {
        // Format the text run whole so emphasis can cross soft line breaks;
        // withBreaks() restores the breaks inside and outside markers.
        out.push(
          <p key={`p-${rKey}`} className="my-1 first:mt-0 last:mb-0">
            {formatInline(run.join('\n'), `p-${rKey}`)}
          </p>
        );
      }
      runStart = idx;
    }
  });

  return out;
}
